import React, { type ForwardedRef, forwardRef, type ComponentPropsWithoutRef } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import {
	TypeSelectWithAddIssueTypeModal,
	type TypeSelectProps,
	type TypeSelectRef,
	useManageIssueTypeOption,
} from '@atlassian/jira-config-in-context-issue-types';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import InlineCardCreate from '@atlassian/jira-platform-inline-card-create';
import { useAddIssueTypeContext } from '@atlassian/jira-project-settings-apps-add-issue-type';
import { REFRESH_SOURCE_ADD_ISSUE_TYPE_MODAL } from '../../../../model/constants';
import { useBacklogDispatch, useBacklogSelector } from '../../../../state';
import { refreshDataRequest } from '../../../../state/actions/app';
import { getIsCompanyManaged } from '../../../../state/selectors/app';
import { manageIssueTypesSelector } from '../../../../state/selectors/card-type';
import { canEditBoardConfig } from '../../../../state/selectors/permissions';

export const IssueTypeCreatableTypeSelect = forwardRef(
	(props: TypeSelectProps, ref: ForwardedRef<TypeSelectRef>) => {
		const dispatch = useBacklogDispatch();
		const isCompanyManaged = useBacklogSelector(getIsCompanyManaged);
		const manageIssueTypesLink = useBacklogSelector((state) => {
			if (canEditBoardConfig(state)) {
				return manageIssueTypesSelector(state);
			}
			return null;
		});
		const manageIssueTypeOption = useManageIssueTypeOption(manageIssueTypesLink);

		const handleNewIssueTypeCreate = () => {
			// handle refresh/dispatch
			dispatch(refreshDataRequest(REFRESH_SOURCE_ADD_ISSUE_TYPE_MODAL));
		};

		return (
			<TypeSelectWrapper shouldShowChevron={isCompanyManaged}>
				<TypeSelectWithAddIssueTypeModal
					{...props}
					ref={ref}
					onNewIssueTypeCreated={handleNewIssueTypeCreate}
					extraOptions={manageIssueTypeOption ?? undefined}
				/>
			</TypeSelectWrapper>
		);
	},
);

const InlineCardCreateWithIssueTypesInContextVariant = ({
	isEligibleInContextIssueTypesExperiment,
	...props
}: ComponentPropsWithoutRef<typeof InlineCardCreate>) => {
	const [{ isActive: isModalOpen }] = useAddIssueTypeContext();

	return (
		<InlineCardCreate
			{...props}
			// Issue type in Context Experiment
			shouldKeepFormOpenOnClickOutside={isModalOpen}
			TypeSelect={IssueTypeCreatableTypeSelect}
			isEligibleInContextIssueTypesExperiment={isEligibleInContextIssueTypesExperiment}
		/>
	);
};

export const InlineCardCreateWithIssueTypesInContext = (
	props: ComponentPropsWithoutRef<typeof InlineCardCreate>,
) => {
	// Variant cohort
	const [expConfig] = UNSAFE_noExposureExp('jsw_config_in_context_issue_types_m2');
	if (expConfig.get('inlineIssueTypeConfigEnabled', false)) {
		return (
			<InlineCardCreateWithIssueTypesInContextVariant
				{...props}
				isEligibleInContextIssueTypesExperiment
			/>
		);
	}

	// Control cohort
	return <InlineCardCreate {...props} isEligibleInContextIssueTypesExperiment />;
};

// NOTE: copy from src/packages/software/backlog/src/view/card-list/inline-card-create/index.tsx
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TypeSelectWrapper = styled.div<{
	shouldShowChevron: boolean;
}>({
	// Spacing tokens are in use
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginLeft: ({ shouldShowChevron }) =>
		shouldShowChevron ? token('space.500', '40px') : token('space.250', '20px'),
	borderLeft: '1px solid transparent', // this is used to match with the border of the card, we can set margin-left to 21px, but we won't be able to migrate to use the space token
});
